import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TenantAuthProvider, useTenantAuth } from "./context/TenantAuthContext";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Properties from "./pages/Properties";
import Landlords from "./pages/Landlords";
import Units from "./pages/Units";
import Tenants from "./pages/Tenants";
import Costs from "./pages/Costs";
import Billings from "./pages/Billings";
import Damages from "./pages/Damages";
import Meters from "./pages/Meters";
import ImportPage from "./pages/Import";
import MieterZugaenge from "./pages/MieterZugaenge";
import MieterLogin from "./pages/MieterLogin";
import MieterDashboard from "./pages/MieterDashboard";
import MieterPasswortVergessen from "./pages/MieterPasswortVergessen";
import MieterPasswortReset from "./pages/MieterPasswortReset";
import Roadmap from "./pages/Roadmap";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import AGB from "./pages/AGB";
import { CookieBanner } from "./components/CookieBanner";

function Spinner() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
    </div>
  );
}

// Protected Route Component (landlord)
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Public Route Component (redirects to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

// Tenant Protected Route
function TenantProtectedRoute({ children }) {
  const { tenantUser, loading } = useTenantAuth();
  if (loading) return <Spinner />;
  if (!tenantUser) return <Navigate to="/mieter/login" replace />;
  return children;
}

// Tenant Public Route
function TenantPublicRoute({ children }) {
  const { tenantUser, loading } = useTenantAuth();
  if (loading) return <Spinner />;
  if (tenantUser) return <Navigate to="/mieter/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Tenant Portal Routes */}
      <Route
        path="/mieter/login"
        element={
          <TenantPublicRoute>
            <MieterLogin />
          </TenantPublicRoute>
        }
      />
      <Route path="/mieter/passwort-vergessen" element={<MieterPasswortVergessen />} />
      <Route path="/mieter/passwort-reset" element={<MieterPasswortReset />} />
      <Route
        path="/mieter/dashboard"
        element={
          <TenantProtectedRoute>
            <MieterDashboard />
          </TenantProtectedRoute>
        }
      />

      {/* Protected Landlord Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/properties"
        element={
          <ProtectedRoute>
            <Properties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/landlords"
        element={
          <ProtectedRoute>
            <Landlords />
          </ProtectedRoute>
        }
      />
      <Route
        path="/units"
        element={
          <ProtectedRoute>
            <Units />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <ProtectedRoute>
            <Tenants />
          </ProtectedRoute>
        }
      />
      <Route
        path="/costs"
        element={
          <ProtectedRoute>
            <Costs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billings"
        element={
          <ProtectedRoute>
            <Billings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/damages"
        element={
          <ProtectedRoute>
            <Damages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meters"
        element={
          <ProtectedRoute>
            <Meters />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute>
            <ImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mieter-zugaenge"
        element={
          <ProtectedRoute>
            <MieterZugaenge />
          </ProtectedRoute>
        }
      />

      {/* Landing Page */}
      <Route path="/" element={<Landing />} />

      {/* Public Roadmap + Changelog (also renders inside Layout for logged-in landlords) */}
      <Route path="/roadmap" element={<Roadmap />} />

      {/* Legal Pages */}
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <TenantAuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <CookieBanner />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Inter, sans-serif'
              }
            }}
          />
        </BrowserRouter>
      </TenantAuthProvider>
    </AuthProvider>
  );
}

export default App;
